<template>
    <div>
        <div class="flex flex-col md:flex-row md:h-12 items-center mb-6 md:mb-2">
            <h2 class="text-md md:text-2xl text-red font-avertaBold"><span class="uppercase">Mina sidor Företag</span> / <span class="font-averta">Kampanjer</span></h2>
        </div>

        <div class="text-sm text-gray mb-10 max-w-2xl">
            Här visas alla de kampanjer som du har, eller har haft på familjekortet.se. Härifrån kan ni t.ex. ändra start- & slutdatum och kampanjkod.
            Det är inte möjligt att byta “Typ av kampanj” eller “Typ av kampanjkod”. Om ni önskar ändra dessa, så behöver ni skapa en ny Kampanj.
        </div>

        <div class="md:flex items-center mb-8">
            <div>
                <input
                    v-on:keyup.enter="searchInput"
                    v-model="filterBySearch"
                    type="text"
                    class="rounded-md ring-0 outline-none border-2 border-blue focus:border-blue-600 py-2 px-4 w-full md:w-64" placeholder="Sök efter kampanj" />
            </div>

            <inertia-link :href="`/kund/${company.slug}/lagg-till-kampanj`" class="ml-auto w-full md:w-auto mt-3 md:mt-0 transition-all self-center text-sm hover:bg-red border border-red text-red hover:text-white rounded-md px-4 py-2 font-semibold tracking-wide inline-block">Skapa kampanj</inertia-link>
        </div>

        <div class="mt-2 mb-6 space-x-2">
            <span
                v-for="status in statuses"
                @click="setFilter('status', status.key)"
                :class="{
                    'text-secondaryBlue bg-blue font-semibold': filterByStatus !== status.key,
                    'font-bold text-secondaryBlue': filterByStatus === status.key,
                }"
                class="inline-block transition-all border-secondaryBlue hover:bg-white text-xs border font-semibold rounded-full px-4 py-2 cursor-pointer select-none">
                    {{ status.label }}
                </span>
        </div>

        <div v-if="campaigns.length === 0" class="flex flex-col items-center justify-center">
            <blank-canvas-illustration class="h-96 w-96"></blank-canvas-illustration>

            <inertia-link :href="`/kund/${company.slug}/lagg-till-kampanj`" class="transition-all text-sm hover:bg-red border border-red text-red hover:text-white rounded-md px-4 py-2 font-semibold tracking-wide">Skapa kampanj</inertia-link>
        </div>

        <div v-if="campaigns.length > 0" class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div v-for="campaign in campaigns" class="bg-white shadow-sm rounded-md relative overflow-hidden">
                <div class="rounded-tr rounded-tl rounded" :class="{ 'bg-gray bg-opacity-10 inset-0': campaign.status === 'cancelled' }">
                    <div
                        v-if="campaign.status === 'active'"
                        class="bg-green-300 text-gray text-center font-semibold tracking-wide text-sm py-2 px-4 w-full">
                        <span class="font-bold font-avertaBlack">{{ campaign.leftValue }}</span> {{ campaign.leftLabel }} kvar
                    </div>

                    <div
                        v-if="campaign.status === 'infinite'"
                        class="bg-green-300 text-gray text-center font-semibold tracking-wide text-sm py-2 px-4 w-full">
                        Aktiv
                    </div>

                    <div
                        v-if="campaign.status === 'expiring'"
                        class="bg-yellow-200 text-gray text-center font-semibold tracking-wide text-sm py-2 px-4 w-full">

                        <span class="font-bold font-avertaBlack">{{ campaign.leftValue }}</span> {{ campaign.leftLabel }} kvar
                    </div>

                    <div v-if="campaign.status === 'cancelled'" class="bg-red text-gray text-center font-semibold tracking-wide text-sm py-2 px-4 w-full">
                        Avslutad
                    </div>

                    <div v-if="campaign.status === 'new'" class="bg-blue text-gray text-center font-semibold tracking-wide text-sm py-2 px-4 w-full">
                        Snart live
                    </div>
                </div>

                <div>
                    <div class="aspect-w-4 aspect-h-3">
                        <img v-if="campaign.main_image" :src="campaign.main_image" />
                        <img v-if="!campaign.main_image" :src="fallbackImage" />
                    </div>

                     <div class="flex justify-center mt-[-52.5px] mb-[-52.5px]">
                        <div class="z-[9] rounded-full bg-white overflow-hidden hover:cursor-pointer w-[105px] h-[105px]">
                            <div class="flex h-full justify-center items-center">
                                <img v-if="campaign.company_logo_image" :src="`${campaign.company_logo_image}?v=${new Date().getTime()}`" />
                            </div>
                        </div>
                    </div>

                    <div class="pt-[52.5px]">
                        <div class="text-center font-avertaLight pt-3 pb-6 text-[16px]">
                            {{ campaign.heading }}
                        </div>
                    </div>

                    <div class="flex items-center justify-center py-6">
                        <inertia-link
                            :href="`/kund/${company.slug}/kampanjer/${campaign.slug}`"
                            class="text-sm bg-white border border-gray transition-all hover:bg-gray hover:text-white text-gray rounded-md px-4 py-2 font-semibold tracking-wide inline-block z-[200]">
                            Ändra kampanj
                        </inertia-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ClientLayout from '@/Layouts/Client';
import { DateTime } from 'luxon';
import { router } from '@inertiajs/vue3';
import { getFallbackImage } from '@/Util/images';
import Popper from 'vue3-popper';
import BlankCanvasIllustration from '@/Illustrations/BlankCanvas';

export default {
    props: ['campaigns', 'initialCampaingsFiltering', 'initialCampaingsSearch', 'company'],
    inject: ['$isMobile'],
    layout: ClientLayout,
    components: { Popper, BlankCanvasIllustration },
    data() {
        return {
            filterByStatus: null,
            filterBySearch: null,
            statuses: {
                active: { key: 'active', label: 'Aktiva' },
                new: { key: 'new', label: 'Snart live' },
                // expiring: { key: 'expiring', label: 'Få dagar kvar' },
                cancelled: { key: 'cancelled', label: 'Avslutade' },
            },
        };
    },
    mounted () {
        this.filterByStatus = this.initialCampaingsFiltering;
        this.filterBySearch = this.initialCampaingsSearch;
    },
    computed: {
        fallbackImage() {
            return getFallbackImage();
        }
    },
    methods: {
        searchInput (e) {
            this.setFilter('search', this.filterBySearch);
        },

        setFilter(filter, value) {
            const params = {};

            if (filter === 'status') {
                this.filterByStatus = (this.filterByStatus === value) ? null : value;
            }

            if (this.filterByStatus) {
                params.status = this.filterByStatus;
            }

            if (this.filterBySearch) {
                params.search = this.filterBySearch;
            }

            router.get(`/kund/${this.$page.props.currentCompany.slug}/kampanjer`, params, { replace: true, preserveState: true });
        },
        startDate(campaign) {
            return DateTime.fromISO(campaign.start_date).setLocale('sv').toFormat('yyyy-MM-dd HH:mm');
        },
        endDateLeft(campaign) {
            if (!campaign.end_date) {
                return null;
            }

            console.log(campaign.end_date)

            let value;
            let unit;
            const diff = DateTime.fromString(campaign.end_date, 'yyyy-MM-dd HH:mm:ss').setLocale('sv').diffNow(['days', 'hours', 'minutes']).toObject();

            if (diff.days > 0) {
                value = diff.hours > 0 ? diff.days + 1 : diff.days;
                unit = 'days';
            } else if (diff.days === 0 && diff.hours > 0) {
                value = diff.hours;
                unit = 'hours';
            } else if (diff.days === 0 && diff.hours === 0) {
                value = diff.minutes;
                unit = 'minutes';
            }

            return { value: parseInt(value), unit: unit };
        }
    }
}
</script>
